import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimesCircle, FaFilePdf, FaSpinner, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Expired.css';
import axios from 'axios';

const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

export default function Selfservice() {
  const navigate = useNavigate();

  // Auth & agent
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  const [formData, setFormData] = useState({
    regNo: '',
    transactionId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionType, setCorrectionType] = useState(null);
  const [correctionData, setCorrectionData] = useState({
    insuredItemOID: null,
    clientId: null,
    correctedValue: '',
    proposalDetails: null,
    insuredItems: null
  });
  const [processingCorrection, setProcessingCorrection] = useState(false);
  const [proposalData, setProposalData] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
    setCertificateUrl(null);
    setProposalData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setCertificateUrl(null);

    try {
      const response = await axios.get(`${API}/selfservice`, {
        params: {
          regNo: formData.regNo,
          transactionId: formData.transactionId
        }
      });

      if (response.data.success) {
        setProposalData(response.data.data);
        
        // Check if certificate already exists in the response
        if (response.data.data.certificatePdf) {
          // Certificate already exists, just show download button
          setCertificateUrl(response.data.data.certificatePdf);
          toast.success('Certificate retrieved successfully!');
          setError(null);
        } else {
          // No existing certificate, call external API to generate one
          await callExternalAPI(response.data.data);
        }
      }
    } catch (err) {
      console.error('Error:', err.response?.data || err.message);
      
      const errorMessage = err.response?.data?.message || '';
      const errorData = err.response?.data?.data;
      
      if (errorMessage.toLowerCase().includes('insured pin is invalid')) {
        setCorrectionType('pin');
        setCorrectionData({
          ...correctionData,
          clientId: errorData?.proposalDetails?.Client,
          proposalDetails: errorData?.proposalDetails,
          insuredItems: errorData?.insuredItems,
          correctedValue: ''
        });
        setShowCorrectionModal(true);
        setError(null);
      } else if (errorMessage.toLowerCase().includes('incorrect vehicle registration year')) {
        setCorrectionType('year');
        setCorrectionData({
          ...correctionData,
          insuredItemOID: errorData?.insuredItems?.[0]?.OID,
          proposalDetails: errorData?.proposalDetails,
          insuredItems: errorData?.insuredItems,
          correctedValue: ''
        });
        setShowCorrectionModal(true);
        setError(null);
      } else {
        setError(errorMessage || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const callExternalAPI = async (data) => {
    try {
      const requestBody = {
        PolicyId: data.insuredItems?.[0]?.OID || data.insuredItems?.OID,
        StartDate: data.proposalDetails?.CommencementDate,
        EndDate: data.proposalDetails?.ExpiryDate,
        Capacity: data.insuredItems?.[0]?.Capacity || data.insuredItems?.Capacity,
        SumInsured: data.insuredItems?.[0]?.SumInsured || data.insuredItems?.SumInsured || 0,
        Tonnage: data.insuredItems?.[0]?.Tonnage || data.insuredItems?.Tonnage
      };

      const response = await axios.post(
        'https://sms.definiteassurance.com/webhook/87c8e2e0-86c5-4749-ab47-2797aae29383',
        requestBody
      );

      if (response.data.succeeded) {
        if (response.data.certificatePdf) {
          setCertificateUrl(response.data.certificatePdf);
          toast.success('Certificate generated successfully!');
          setError(null);
        } else {
          setError('Certificate generated but no download link provided.');
        }
      } else {
        setError(response.data.errorMessage || 'Certificate generation failed');
      }
    } catch (err) {
      console.error('External API Error:', err);
      setError('Failed to generate certificate. Please try again.');
    }
  };

  const handleCorrectionSubmit = async () => {
    setProcessingCorrection(true);
    
    try {
      let endpoint = '';
      let payload = {};

      if (correctionType === 'pin') {
        endpoint = `${API}/update-client-pin`;
        payload = {
          clientId: correctionData.clientId,
          correctedPin: correctionData.correctedValue
        };
      } else if (correctionType === 'year') {
        endpoint = `${API}/update-vehicle-year`;
        payload = {
          insuredItemOID: correctionData.insuredItemOID,
          correctedYear: parseInt(correctionData.correctedValue)
        };
      }

      const updateResponse = await axios.post(endpoint, payload);
      
      if (updateResponse.data.success) {
        setShowCorrectionModal(false);
        toast.success('Information updated successfully!');
        setLoading(true);
        
        try {
          const requestBody = {
            PolicyId: correctionData.insuredItems?.[0]?.OID || correctionData.insuredItemOID,
            StartDate: correctionData.proposalDetails?.CommencementDate,
            EndDate: correctionData.proposalDetails?.ExpiryDate,
            Capacity: correctionData.insuredItems?.[0]?.Capacity,
            SumInsured: correctionData.insuredItems?.[0]?.SumInsured || 0,
            Tonnage: correctionData.insuredItems?.[0]?.Tonnage
          };

          const apiResponse = await axios.post(
            'https://sms.definiteassurance.com/webhook/87c8e2e0-86c5-4749-ab47-2797aae29383',
            requestBody
          );

          if (apiResponse.data.succeeded) {
            if (apiResponse.data.certificatePdf) {
              setCertificateUrl(apiResponse.data.certificatePdf);
              toast.success('Certificate generated successfully after correction!');
              setError(null);
            } else {
              setError('Certificate generated but no download link provided.');
            }
          } else {
            setError(apiResponse.data.errorMessage || 'Certificate generation failed after correction');
          }
        } catch (err) {
          setError('Failed to generate certificate after correction. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        setError(updateResponse.data.message || 'Failed to update information. Please try again.');
        setShowCorrectionModal(false);
      }
    } catch (err) {
      console.error('Correction Error:', err);
      setError('Failed to update information. Please try again.');
      setShowCorrectionModal(false);
    } finally {
      setProcessingCorrection(false);
    }
  };

  const handleDownloadCertificate = () => {
    if (certificateUrl) {
      window.open(certificateUrl, '_blank');
    }
  };

  return (
    <div className="expired-page">
      <Sidebar />
      <div className="expired-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="expired-top-row">
          <span className="expired-company">Definite Assurance</span>
          <span className="expired-breadcrumb">Dashboard / Self Service</span>
        </div>

        <div className="expired-card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ 
              width: '70px', 
              height: '70px', 
              backgroundColor: '#1a5c5a', 
              borderRadius: '50%', 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <FaFilePdf size={32} color="white" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
              Self Service 
            </h2>
          
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                Registration Number
              </label>
              <input
                type="text"
                name="regNo"
                value={formData.regNo}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#1a5c5a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                placeholder="Enter registration number"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                MPESA CODE (Transaction ID)
              </label>
              <input
                type="text"
                name="transactionId"
                value={formData.transactionId}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#1a5c5a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                placeholder="Enter transaction ID"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#ccc' : '#1a5c5a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#134b49')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#1a5c5a')}
            >
              {loading ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>
                  <FaSearch />
                  Submit Request
                </>
              )}
            </button>
          </form>

          {/* Proposal Details */}
          {proposalData && (
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              borderLeft: `4px solid #1a5c5a`
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a5c5a', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaCheckCircle />
                Policy Details
              </h3>
              <div style={{ fontSize: '14px', color: '#555', display: 'grid', gap: '8px' }}>
                <p><strong>Registration:</strong> {proposalData.proposalDetails?.Narration}</p>
                <p><strong>Commencement:</strong> {proposalData.proposalDetails?.CommencementDate}</p>
                <p><strong>Expiry:</strong> {proposalData.proposalDetails?.ExpiryDate}</p>
                {proposalData.insuredItems?.length > 0 && (
                  <>
                    <p><strong>Capacity:</strong> {proposalData.insuredItems[0]?.Capacity}</p>
                    <p><strong>Tonnage:</strong> {proposalData.insuredItems[0]?.Tonnage}</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{ 
              marginTop: '20px', 
              padding: '12px', 
              backgroundColor: '#fee', 
              borderRadius: '8px',
              borderLeft: '4px solid #dc3545',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FaExclamationTriangle size={20} color="#dc3545" />
              <span style={{ color: '#dc3545', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Success Message with Download Button */}
          {certificateUrl && (
            <div style={{ 
              marginTop: '20px', 
              padding: '16px', 
              backgroundColor: '#e8f5e9', 
              borderRadius: '8px',
              borderLeft: '4px solid #4caf50',
              textAlign: 'center'
            }}>
              <p style={{ color: '#2e7d32', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <FaCheckCircle />
                Certificate ready for download!
              </p>
              <button
                onClick={handleDownloadCertificate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <FaDownload />
                Download Certificate
              </button>
            </div>
          )}
        </div>

        {/* Correction Modal */}
        {showCorrectionModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {correctionType === 'pin' ? 'Correct Insured PIN' : 'Correct Vehicle Registration Year'}
                </h3>
                <button
                  onClick={() => setShowCorrectionModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}
                >
                  ×
                </button>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#fff3cd',
                borderRadius: '6px',
                marginBottom: '20px',
                borderLeft: '4px solid #ffc107'
              }}>
                <p style={{ color: '#856404', fontSize: '14px', margin: 0 }}>
                  {correctionType === 'pin' 
                    ? 'The insured PIN provided is invalid. Please enter the correct PIN for the client.'
                    : 'The vehicle registration year is invalid. Please enter the correct year.'}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                  {correctionType === 'pin' ? 'Corrected PIN' : 'Corrected Year'}
                </label>
                <input
                  type={correctionType === 'pin' ? 'text' : 'number'}
                  value={correctionData.correctedValue}
                  onChange={(e) => setCorrectionData({
                    ...correctionData,
                    correctedValue: e.target.value
                  })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  placeholder={correctionType === 'pin' ? 'Enter correct PIN' : 'Enter correct year'}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowCorrectionModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCorrectionSubmit}
                  disabled={processingCorrection || !correctionData.correctedValue}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: processingCorrection || !correctionData.correctedValue ? '#ccc' : '#1a5c5a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: processingCorrection || !correctionData.correctedValue ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {processingCorrection ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    'Submit Correction'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}